import type { BracketCompetitor, BracketRound } from "./bracketLayout";
import { standardMatchAnchors } from "./bracketSeeding";

/**
 * Preview visual del paso Formato: dado el tamaño de bracket configurado y los
 * inscritos reales, construye un cuadro dummy (mismas posiciones estándar que
 * la generación real) + resumen + sugerencia. Refleja la semántica histórica
 * de eliminationPlanForBracket: "Ronda D" es la primera ronda; si los
 * inscritos no la llenan, una previa parcial alimenta el cuadro completo.
 */

export interface EliminationPlan {
  preliminaryMatches: number;
  directAdvancers: number;
  nextRoundSize: number;
  hasManualByeCount: boolean;
}

export function eliminationPlanForBracket(
  playerCount: number | null,
  bracketSize: number,
  manualByeCount: number | null = null,
): EliminationPlan {
  const halfRoundSize = bracketSize > 2 ? bracketSize / 2 : 1;
  if (!playerCount || playerCount <= 0) {
    return {
      preliminaryMatches: 0,
      directAdvancers: Math.max(0, Number(manualByeCount) || 0),
      nextRoundSize: halfRoundSize,
      hasManualByeCount: manualByeCount !== null,
    };
  }
  if (playerCount > bracketSize) {
    const preliminaryMatches = Math.max(playerCount - bracketSize, 0);
    return {
      preliminaryMatches,
      directAdvancers: Math.max(bracketSize - preliminaryMatches, 0),
      nextRoundSize: bracketSize,
      hasManualByeCount: false,
    };
  }
  if (manualByeCount !== null) {
    const safeByeCount = Math.max(0, Math.min(Number(manualByeCount) || 0, playerCount));
    return {
      preliminaryMatches: Math.max(Math.floor((playerCount - safeByeCount) / 2), 0),
      directAdvancers: safeByeCount,
      nextRoundSize: halfRoundSize,
      hasManualByeCount: true,
    };
  }
  if (playerCount <= halfRoundSize) {
    return { preliminaryMatches: 0, directAdvancers: 0, nextRoundSize: halfRoundSize, hasManualByeCount: false };
  }
  return {
    preliminaryMatches: Math.max(playerCount - halfRoundSize, 0),
    directAdvancers: Math.max(bracketSize - playerCount, 0),
    nextRoundSize: halfRoundSize,
    hasManualByeCount: false,
  };
}

export interface EliminationFormatSuggestion {
  tone: "warning" | "info";
  text: string;
}

export interface EliminationFormatPreview {
  rounds: BracketRound[];
  totalMatches: number;
  playInMatches: number;
  restingPlayers: number;
  suggestion: EliminationFormatSuggestion | null;
}

const PREVIEW_SIZES = [2, 4, 8, 16, 32, 64, 128, 256];

function isPowerOfTwo(value: number): boolean {
  return value >= 1 && (value & (value - 1)) === 0;
}

function seedCompetitor(seed: number): BracketCompetitor {
  return { name: `Sembrado ${seed}`, seed, games: [], isWinner: false };
}

function previewRoundLabel(size: number): string {
  if (size <= 2) return "Final";
  if (size === 4) return "Semis";
  if (size === 8) return "Cuartos";
  return `Ronda ${size}`;
}

function pendingRounds(entrySize: number): BracketRound[] {
  const rounds: BracketRound[] = [];
  let size = entrySize;
  while (size >= 2) {
    rounds.push({
      label: previewRoundLabel(size),
      matches: Array.from({ length: size / 2 }, (_, index) => ({
        id: `preview-${size}-${index + 1}`,
        top: null,
        bottom: null,
        pending: true,
      })),
    });
    size = size / 2;
  }
  return rounds;
}

function smallestSizeThatFits(playerCount: number): number {
  return PREVIEW_SIZES.find((size) => size >= playerCount) ?? PREVIEW_SIZES[PREVIEW_SIZES.length - 1];
}

/** Talla default del bracket cuando NO hay config guardada: la que mas le
 * queda a los inscritos (pedido Eduardo 2026-07-28: con 12 inscritos el
 * Formato abria en Ronda 128). Sin conteo confiable, 128 como siempre. */
export function suggestedBracketSize(playerCount: number | null | undefined): number {
  if (!playerCount || playerCount < 2) return 128;
  return smallestSizeThatFits(playerCount);
}

export function eliminationFormatPreview(
  bracketSize: number,
  playerCount: number | null,
  manualByeCount: number | null = null,
): EliminationFormatPreview | null {
  if (!Number.isFinite(bracketSize) || bracketSize < 2 || !isPowerOfTwo(bracketSize)) return null;
  const n = playerCount && playerCount > 0 ? Math.floor(playerCount) : null;

  if (n === null || n < 2) {
    // Sin inscritos: el cuadro ideal completo del tamaño configurado.
    const first: BracketRound = {
      label: previewRoundLabel(bracketSize),
      matches: standardMatchAnchors(bracketSize / 2).map((anchor, index) => ({
        id: `preview-${bracketSize}-${index + 1}`,
        top: seedCompetitor(anchor),
        bottom: seedCompetitor(bracketSize + 1 - anchor),
        pending: true,
      })),
    };
    return {
      rounds: [first, ...pendingRounds(bracketSize / 2)],
      totalMatches: bracketSize - 1,
      playInMatches: 0,
      restingPlayers: 0,
      suggestion: {
        tone: "info",
        text: "Aún no hay inscritos: este es el cuadro ideal. El preview se ajusta solo con los jugadores reales.",
      },
    };
  }

  if (bracketSize === 2) {
    return {
      rounds: [{
        label: "Final",
        matches: [{
          id: "preview-2-1",
          top: seedCompetitor(1),
          bottom: n >= 2 ? seedCompetitor(2) : null,
          bottomSource: n >= 2 ? undefined : "Descanso",
          pending: true,
        }],
      }],
      totalMatches: 1,
      playInMatches: 0,
      restingPlayers: 0,
      suggestion: n > 2
        ? { tone: "warning", text: `Con ${n} inscrito(s) la Final directa no alcanza. Sugerido: Ronda ${smallestSizeThatFits(n)}.` }
        : null,
    };
  }

  const plan = eliminationPlanForBracket(n, bracketSize, manualByeCount);
  const playIn = plan.preliminaryMatches;
  const directs = plan.directAdvancers;

  // Ronda 16 parcial (semántica de producción): 13 en Ronda 16 son 5 partidos
  // + 3 descansos DENTRO de la plantilla de 16, no una previa colapsada.
  const partialWithinTemplate = bracketSize === 16 && n <= bracketSize && n > bracketSize / 2 && !plan.hasManualByeCount;

  if (playIn > 0 && !partialWithinTemplate) {
    const entrySize = directs + playIn;
    if (entrySize < 2 || !isPowerOfTwo(entrySize) || n - directs !== playIn * 2) {
      return {
        rounds: [],
        totalMatches: Math.max(n - 1, 0),
        playInMatches: playIn,
        restingPlayers: directs,
        suggestion: {
          tone: "warning",
          text: `Con ${n} inscrito(s) este tamaño no alcanza. Selecciona Ronda ${smallestSizeThatFits(n)} o más grande.`,
        },
      };
    }
    // Previa colapsada: los 2·playIn más débiles, mejor vs peor del subgrupo.
    const preRound: BracketRound = {
      label: "Pre",
      matches: Array.from({ length: playIn }, (_, index) => ({
        id: `preview-pre-${index + 1}`,
        top: seedCompetitor(directs + index + 1),
        bottom: seedCompetitor(n - index),
        pending: true,
      })),
    };
    const entryRound: BracketRound = {
      label: previewRoundLabel(entrySize),
      matches: standardMatchAnchors(entrySize / 2).map((anchor, index) => {
        const rival = entrySize + 1 - anchor;
        const side = (line: number) =>
          line <= directs
            ? { competitor: seedCompetitor(line), source: undefined }
            : { competitor: null, source: `G M${line - directs}` };
        const top = side(anchor);
        const bottom = side(rival);
        return {
          id: `preview-${entrySize}-${index + 1}`,
          top: top.competitor,
          bottom: bottom.competitor,
          topSource: top.source,
          bottomSource: bottom.source,
          pending: true,
        };
      }),
    };
    return {
      rounds: [preRound, entryRound, ...pendingRounds(entrySize / 2)],
      totalMatches: n - 1,
      playInMatches: playIn,
      restingPlayers: directs,
      suggestion: directs > 0
        ? {
          tone: "info",
          text: `${n} inscrito(s): ${playIn * 2} juegan la previa y ${directs} descansan — quedan colocados en el cuadro de ${entrySize} desde el inicio.`,
        }
        : {
          tone: "info",
          text: `Cuadro completo: los ${n} inscrito(s) juegan la primera ronda (${playIn} partido(s)).`,
        },
    };
  }

  // Cuadro con BYEs: los sembrados de arriba descansan dentro de la plantilla.
  // Fuera del caso parcial de Ronda 16, el cuadro real es el más chico donde
  // caben los inscritos (la generación lo auto-ajusta igual).
  const effectiveSize = smallestSizeThatFits(n);
  const templateSize = partialWithinTemplate ? bracketSize : Math.max(effectiveSize, 2);
  const byeCount = Math.max(templateSize - n, 0);
  const first: BracketRound = {
    label: previewRoundLabel(templateSize),
    matches: standardMatchAnchors(templateSize / 2).map((anchor, index) => {
      const rival = templateSize + 1 - anchor;
      const side = (line: number) =>
        line <= n
          ? { competitor: seedCompetitor(line), source: undefined }
          : { competitor: null, source: "Descanso" };
      const top = side(anchor);
      const bottom = side(rival);
      return {
        id: `preview-${templateSize}-${index + 1}`,
        top: top.competitor,
        bottom: bottom.competitor,
        topSource: top.source,
        bottomSource: bottom.source,
        pending: true,
      };
    }),
  };
  const suggestion: EliminationFormatSuggestion | null = !partialWithinTemplate && templateSize < bracketSize
    ? {
      tone: "warning",
      text: `Con ${n} inscrito(s) el cuadro real empieza en Ronda ${templateSize}, no en Ronda ${bracketSize}. Sugerido: Ronda ${templateSize}.`,
    }
    : byeCount > 0
      ? {
        tone: "info",
        text: `${n} inscrito(s) en cuadro de ${templateSize}: ${byeCount} descanso(s) para los mejores sembrados.`,
      }
      : null;
  return {
    rounds: [first, ...pendingRounds(templateSize / 2)],
    totalMatches: n - 1,
    playInMatches: 0,
    restingPlayers: byeCount,
    suggestion,
  };
}

export interface RoundRobinFormatPreview {
  groups: Array<{ name: string; size: number; matches: number }>;
  totalGroupMatches: number;
  jornadas: number;
  playoffQualifiers: number | null;
}

const GROUP_LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

export function roundRobinFormatPreview(options: {
  groupCount: number;
  playersPerGroup: number;
  roundsPerPair: 1 | 2;
  totalPlayers?: number | null;
  playoffQualifiers?: number | null;
}): RoundRobinFormatPreview | null {
  const groupCount = Math.floor(options.groupCount);
  const playersPerGroup = Math.floor(options.playersPerGroup);
  if (!Number.isFinite(groupCount) || groupCount < 1) return null;
  if (!Number.isFinite(playersPerGroup) || playersPerGroup < 2) return null;
  const vueltas = options.roundsPerPair === 2 ? 2 : 1;

  const total = options.totalPlayers && options.totalPlayers > 0 ? Math.floor(options.totalPlayers) : null;
  const sizes: number[] = [];
  if (total !== null && total >= groupCount * 2) {
    const base = Math.floor(total / groupCount);
    const remainder = total % groupCount;
    for (let index = 0; index < groupCount; index += 1) {
      sizes.push(base + (index < remainder ? 1 : 0));
    }
  } else {
    for (let index = 0; index < groupCount; index += 1) sizes.push(playersPerGroup);
  }

  const groups = sizes.map((size, index) => ({
    name: `Grupo ${GROUP_LETTERS[index % GROUP_LETTERS.length]}`,
    size,
    matches: (size * (size - 1) / 2) * vueltas,
  }));
  const largest = Math.max(...sizes);
  const jornadas = largest > 1 ? (largest % 2 === 0 ? largest - 1 : largest) * vueltas : 0;
  return {
    groups,
    totalGroupMatches: groups.reduce((sum, group) => sum + group.matches, 0),
    jornadas,
    playoffQualifiers: options.playoffQualifiers ?? null,
  };
}
